/* ════════════════════════════════════════════════════════════════════
 *  MDL Studies — static reference data.
 *
 *  The prototype's seeded studies/list/in-memory caches have been REMOVED —
 *  the grid, study detail and create/Save all go through the real API
 *  (dbo.AI_MdlStudy* via api.ts).  What remains here:
 *   • Query cascades (Test → Methods → Parameters) and the SDMS retrieve
 *     dataset shown on the New-Study Retrieve step (the MDL is computed
 *     server-side; wiring this to real QCResults is a follow-up).
 *   • Shared grid types (ListRow / PillStatus).
 * ════════════════════════════════════════════════════════════════════ */
import type { RetrieveSpike, RetrieveBlank } from './mdlCalc';

/* ── Query cascades (Test → Methods → Parameters) ── */
export const Q_TEST_METHODS: Record<string, string[]> = {
  VOC: ['EPA 524.2', 'EPA 8260D', 'EPA 524.4'],
  MET: ['EPA 200.8', 'EPA 6020B', 'EPA 200.7'],
  ANI: ['EPA 300.0', 'EPA 300.1', 'SM 4110'],
  NUT: ['EPA 353.2', 'EPA 365.1', 'SM 4500-NO3'],
  THM: ['EPA 524.2-THM', 'EPA 551.1'],
  HAA: ['EPA 552.3', 'EPA 552.2'],
};
export const Q_METHOD_PARAMS: Record<string, string[]> = {
  'EPA 524.2': ['Benzene', 'Toluene', 'Ethylbenzene', 'Xylene-m', 'Xylene-o', 'Xylene-p', 'TCE', 'PCE', 'Chloroform', '1,1-DCE', 'cis-1,2-DCE', 'Vinyl Chloride', 'Carbon Tetrachloride', '1,2-DCA', '1,1,1-TCA', 'Bromoform', 'Dibromochloromethane', 'Bromodichloromethane', 'Styrene', 'Naphthalene'],
  'EPA 8260D': ['Benzene', 'Toluene', 'Ethylbenzene', 'Total Xylenes', 'TCE', 'PCE', 'Vinyl Chloride', '1,2-DCE', 'Methylene Chloride', 'Acetone', '2-Butanone', 'Carbon Disulfide'],
  'EPA 524.4': ['Benzene', 'Toluene', 'Ethylbenzene', 'Xylene-m', 'TCE', 'PCE', 'Chloroform', 'Vinyl Chloride'],
  'EPA 200.8': ['Arsenic', 'Lead', 'Cadmium', 'Chromium', 'Copper', 'Nickel', 'Selenium', 'Mercury', 'Zinc', 'Iron', 'Manganese', 'Barium'],
  'EPA 6020B': ['Arsenic', 'Lead', 'Cadmium', 'Chromium', 'Antimony', 'Beryllium', 'Cobalt', 'Molybdenum', 'Silver', 'Thallium'],
  'EPA 200.7': ['Calcium', 'Magnesium', 'Sodium', 'Potassium', 'Iron', 'Manganese', 'Aluminum', 'Boron'],
  'EPA 300.0': ['Fluoride', 'Chloride', 'Nitrite', 'Nitrate', 'Phosphate', 'Sulfate', 'Bromide'],
  'EPA 300.1': ['Fluoride', 'Chloride', 'Nitrite', 'Nitrate', 'Sulfate', 'Bromide', 'Chlorate', 'Chlorite'],
  'SM 4110': ['Fluoride', 'Chloride', 'Nitrate', 'Sulfate'],
  'EPA 353.2': ['Nitrate', 'Nitrite', 'Nitrate+Nitrite'],
  'EPA 365.1': ['Total Phosphorus', 'Orthophosphate'],
  'SM 4500-NO3': ['Nitrate', 'Ammonia'],
  'EPA 524.2-THM': ['Chloroform', 'Bromodichloromethane', 'Dibromochloromethane', 'Bromoform'],
  'EPA 551.1': ['Chloroform', 'Bromodichloromethane', 'Dibromochloromethane', 'Bromoform', 'Chlorodibromomethane'],
  'EPA 552.3': ['Monochloroacetic acid', 'Dichloroacetic acid', 'Trichloroacetic acid', 'Monobromoacetic acid', 'Dibromoacetic acid'],
  'EPA 552.2': ['Monochloroacetic acid', 'Dichloroacetic acid', 'Trichloroacetic acid', 'Bromoacetic acid'],
};

/* ── Grid row shape (kept identical to the prototype design) ── */
export type PillStatus = 'Released' | 'Approved' | 'Validated' | 'Submitted' | 'Pending Submission' | 'Draft';
export interface ListRow {
  key: string; mdlId: string; matrix: string; test: string; method: string; instrument: string;
  model: 'Initial' | 'Verification'; ver: string; finalMdl: string;
  spikes: string; blanks: string; days: string; pair: boolean;
  studyDate: string; effective: string; nextDue: string; nextDueOverdue?: boolean;
  qc: { label: string; cls: string } | null;
  active: { label: string; cls: string };
  status: PillStatus;
  createdBy: string;
  /** Electronic-signature count from the API (real studies); falls back to a
   *  status-derived count when absent. */
  esig?: number;
}

/* ── Illustrative SDMS retrieve dataset (Nitrate) — shown on the Retrieve
 *    step of the New-Study create flow.  (The write/QC-retrieve path that
 *    reads real QC results is Phase 2.) ── */
export const RETRIEVE_SPIKES: RetrieveSpike[] = [
  { batch: 'B24-0312', sample: 'SPK-001', date: '2024-03-12', spike: 0.10, result: 0.096 },
  { batch: 'B24-0312', sample: 'SPK-002', date: '2024-03-12', spike: 0.10, result: 0.103 },
  { batch: 'B24-0313', sample: 'SPK-003', date: '2024-03-13', spike: 0.10, result: 0.098 },
  { batch: 'B24-0313', sample: 'SPK-004', date: '2024-03-13', spike: 0.10, result: 0.091 },
  { batch: 'B24-0314', sample: 'SPK-005', date: '2024-03-14', spike: 0.10, result: 0.107 },
  { batch: 'B24-0314', sample: 'SPK-006', date: '2024-03-14', spike: 0.10, result: 0.099 },
  { batch: 'B24-0315', sample: 'SPK-007', date: '2024-03-15', spike: 0.10, result: 0.094 },
];
export const RETRIEVE_BLANKS: Array<RetrieveBlank & { note?: string }> = [
  { batch: 'B24-0312', sample: 'BLK-001', date: '2024-03-12', result: 0.011 },
  { batch: 'B24-0312', sample: 'BLK-002', date: '2024-03-12', result: 0.042, note: 'Highest blank' },
  { batch: 'B24-0313', sample: 'BLK-003', date: '2024-03-13', result: 0.008 },
  { batch: 'B24-0313', sample: 'BLK-004', date: '2024-03-13', result: 0.019 },
  { batch: 'B24-0314', sample: 'BLK-005', date: '2024-03-14', result: 0.024 },
  { batch: 'B24-0314', sample: 'BLK-006', date: '2024-03-14', result: 0.013 },
  { batch: 'B24-0315', sample: 'BLK-007', date: '2024-03-15', result: 0.031 },
];

export const INSTRUMENTS = ['— Select —', 'Purge & Trap GC/MS #1', 'Purge & Trap GC/MS #2', 'ICP-MS NexION 2000', 'ICS-6000', 'UV-7000'];
export const MATRICES = ['DW — Drinking Water', 'GW — Groundwater', 'WW — Wastewater', 'SW — Surface Water', 'Reagent Water'];
export const TEST_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'VOC', label: 'VOC — Volatile Organic Compounds' },
  { value: 'MET', label: 'MET — Metals' },
  { value: 'ANI', label: 'ANI — Anions (IC)' },
  { value: 'NUT', label: 'NUT — Nutrients' },
  { value: 'THM', label: 'THM — Trihalomethanes' },
  { value: 'HAA', label: 'HAA — Haloacetic Acids' },
];
